import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Trash2, Users } from 'lucide-react';
import { API, getAuthHeaders } from '../../utils/adminAuth';

export default function CustomerModal({ customer, onClose, onSave }) {
  const [form, setForm] = useState({
    name: customer?.name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
  });
  const [newPassword, setNewPassword] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [verifyResult, setVerifyResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (newPassword.trim()) payload.new_password = newPassword;
      await axios.put(`${API}/admin/customers/${customer.email}`, payload, { headers: getAuthHeaders() });
      toast.success('Customer updated');
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const handleVerify = async () => {
    if (!verifyPassword.trim()) return;
    try {
      const res = await axios.post(`${API}/admin/customers/${customer.email}/verify-password`, { password: verifyPassword }, { headers: getAuthHeaders() });
      setVerifyResult(res.data);
    } catch { toast.error('Failed to verify'); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete customer ${customer.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/customers/${customer.email}`, { headers: getAuthHeaders() });
      toast.success('Customer deleted');
      onSave();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="customer-modal">
      <div className="bg-[#141414] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Manage Customer
          </h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-3 mb-5 pb-4 border-b border-white/10">
          {customer.picture ? (
            <img src={customer.picture} alt="" className="w-12 h-12 rounded-full" />
          ) : (
            <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-white/20" />
            </div>
          )}
          <div>
            <p className="text-white text-sm">{customer.name || customer.email}</p>
            <span className="text-[10px] uppercase px-1.5 py-0.5 border font-sans" style={{
              borderColor: customer.auth_provider === 'google' ? '#4285F4' : '#D4AF37',
              color: customer.auth_provider === 'google' ? '#4285F4' : '#D4AF37',
            }}>{customer.auth_provider || 'email'}</span>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-white/40 text-xs uppercase tracking-wider mb-1 font-sans">Full Name</p>
            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none"
              data-testid="customer-name-input" />
          </div>

          <div>
            <p className="text-white/40 text-xs uppercase tracking-wider mb-1 font-sans">Email</p>
            <p className="text-white text-sm py-2">{customer.email}</p>
          </div>

          <div>
            <p className="text-white/40 text-xs uppercase tracking-wider mb-1 font-sans">Phone</p>
            <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
              placeholder="Not set"
              className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
              data-testid="customer-phone-input" />
          </div>

          <div className="border-t border-white/10 pt-4">
            <p className="text-white/40 text-xs uppercase tracking-wider mb-2 font-sans">Verify Customer Password</p>
            <div className="flex gap-2">
              <input type="text" value={verifyPassword} onChange={e => { setVerifyPassword(e.target.value); setVerifyResult(null); }}
                placeholder="Type password to check"
                className="flex-1 bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
                data-testid="verify-password-input" />
              <button onClick={handleVerify} className="text-xs text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1.5 hover:border-[#D4AF37] transition-colors" data-testid="verify-password-btn">
                Check
              </button>
            </div>
            {verifyResult && (
              <p className={`text-xs mt-1 font-sans ${verifyResult.match ? 'text-green-400' : 'text-red-400'}`} data-testid="verify-result">
                {verifyResult.message}
              </p>
            )}
          </div>

          <div>
            <p className="text-white/40 text-xs uppercase tracking-wider mb-1 font-sans">Set New Password</p>
            <input type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              placeholder="Leave blank to keep current"
              className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
              data-testid="new-password-input" />
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={handleSave} disabled={saving}
            className="flex-1 bg-[#D4AF37] text-black py-2.5 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50"
            data-testid="save-customer-btn">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button onClick={handleDelete}
            className="px-4 py-2.5 border border-red-500/30 text-red-400 text-sm hover:border-red-500 transition-colors"
            data-testid="delete-customer-btn">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
